import { useSearchAnything, useInMobileView } from "../../hooks"
import PageSearchForm from "./page-search-form"
import SearchResultsBody from "./search-results-body"
import MobileSearchSheet from "./mobile-search-sheet"
import MobileSearchTrigger from "./mobile-search-trigger"
import { useEffect, useRef, useState, useMemo, useCallback } from "react"
import { classNames } from "../../utils/classnames"

interface Props {
  handleMasquerade: (loid: string) => void
  isAdmin: boolean
}

const SearchAnything = ({ isAdmin, handleMasquerade }: Props) => {
  const listboxId = "page-search-listbox"
  const inMobileView = useInMobileView()
  const {
    canUsePrefixes,
    isOpen,
    isLoadingResults,
    query,
    setQuery,
    searchResults,
    recentSearchesData,
    open,
    close,
    onFocusInput,
    onClickItem,
    onClickResetRecentSearches
  } = useSearchAnything()

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  // Derive items array for navigation
  const items = useMemo(() => {
    if (query?.length >= 3) {
      return searchResults
    }
    // For recent searches, add 1 to count for the Clear button
    return recentSearchesData
  }, [query, searchResults, recentSearchesData])

  // Total navigable items (including Clear button for recent searches)
  const totalNavigableItems = useMemo(() => {
    if (query?.length >= 3) {
      return searchResults.length
    }
    // Add 1 for Clear button if recent searches exist
    return recentSearchesData.length > 0 ? recentSearchesData.length + 1 : 0
  }, [query, searchResults.length, recentSearchesData.length])

  // Reset active index when items change or dropdown closes
  useEffect(() => {
    setActiveIndex(-1)
  }, [items, isOpen])

  // Focus management for active item
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex < totalNavigableItems) {
      itemRefs.current[activeIndex]?.focus()
      itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" })
    }
  }, [activeIndex, totalNavigableItems])

  // Close dropdown when clicking outside. The mobile sheet is portalled
  // outside containerRef and covers the screen, so it manages its own close.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        close()
      }
    }

    if (isOpen && !inMobileView) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, inMobileView, close])

  // Handle Escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        close()
        inputRef.current?.blur()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, close])

  // Global Cmd/Ctrl+K handler
  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        open()
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
      }
    }

    document.addEventListener("keydown", handleGlobalShortcut)
    return () => document.removeEventListener("keydown", handleGlobalShortcut)
  }, [open])

  // Handle arrow navigation on input
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && totalNavigableItems > 0) {
      event.preventDefault()
      setActiveIndex(0)
    } else if (event.key === "Tab" && !inMobileView) {
      // Close dropdown and allow Tab to move focus naturally.
      // In the sheet, Tab should reach the Cancel button instead.
      close()
    }
  }

  // Handle arrow navigation on items
  const handleItemKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (index < totalNavigableItems - 1) {
        setActiveIndex(index + 1)
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (index === 0) {
        setActiveIndex(-1)
        inputRef.current?.focus()
      } else {
        setActiveIndex(index - 1)
      }
    } else if (event.key === "Enter") {
      event.preventDefault()

      // For recent searches, index 0 is the Clear button (handled in RecentSearches)
      // For search results, select the item directly
      if (query?.length >= 3) {
        onClickItem(items[index])
      } else {
        // For recent searches, indices start at 1 for actual items
        if (index > 0) {
          onClickItem(items[index - 1])
        }
        // Index 0 (Clear button) is handled in RecentSearches component
      }
    } else if (event.key === "Tab" && !inMobileView) {
      // Close dropdown and allow Tab to move focus naturally
      close()
      // Don't prevent default - let Tab work normally
    }
  }

  // Drop a `prefix:` into the input and hand focus back so the next
  // keystrokes are the ID.
  const onPickPrefix = useCallback(
    (prefix: string) => {
      setQuery(`${prefix}:`)
      inputRef.current?.focus()
    },
    [setQuery]
  )

  const resultsBody = (
    <SearchResultsBody
      query={query}
      isLoadingResults={isLoadingResults}
      searchResults={searchResults}
      recentSearches={recentSearchesData}
      activeIndex={activeIndex}
      itemRefs={itemRefs}
      isAdmin={isAdmin}
      comfortable={inMobileView}
      showPrefixChips={inMobileView && canUsePrefixes}
      onClickItem={onClickItem}
      onClickResetRecentSearches={onClickResetRecentSearches}
      onPickPrefix={onPickPrefix}
      handleMasquerade={handleMasquerade}
      close={close}
      onItemKeyDown={handleItemKeyDown}
    />
  )

  if (inMobileView) {
    return (
      <div ref={containerRef} className="w-full flex items-center">
        <MobileSearchTrigger isOpen={isOpen} onClick={open} />
        <MobileSearchSheet
          open={isOpen}
          onClose={close}
          initialFocus={inputRef}
          header={
            <div className="flex items-center gap-1">
              <PageSearchForm
                ref={inputRef}
                variant="sheet"
                query={query}
                setQuery={setQuery}
                onFocus={onFocusInput}
                onKeyDown={handleInputKeyDown}
                controlsId={listboxId}
                isOpen={isOpen}
              />
              <button
                type="button"
                onClick={close}
                className="shrink-0 rounded-md px-2 py-2 text-sm font-medium text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/40"
              >
                Cancel
              </button>
            </div>
          }
        >
          <div id={listboxId} role="listbox">
            {resultsBody}
          </div>
        </MobileSearchSheet>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full flex items-center text-gray-400 focus-within:text-gray-600 sm:max-w-[calc(42rem*0.75)]"
    >
      <PageSearchForm
        ref={inputRef}
        query={query}
        setQuery={setQuery}
        onFocus={onFocusInput}
        onKeyDown={handleInputKeyDown}
        controlsId={listboxId}
        isOpen={isOpen}
      />

      <div
        id={listboxId}
        aria-hidden={!isOpen}
        role="listbox"
        className={classNames(
          // layout
          "absolute top-full left-0 z-50 w-full overflow-hidden -mt-1.5",
          // border
          "border border-neutral-200 rounded-lg",
          // background
          "bg-white",
          // effects
          "shadow-2xl transition duration-200 ease-out transform-gpu",
          // states
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-3 pointer-events-none"
        )}
      >
        <div className="max-h-80 overflow-auto">{resultsBody}</div>
      </div>
    </div>
  )
}

export default SearchAnything
